import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { bookingService, orderService, settingsService, teamService } from '../../services/api';
import { applyAccent } from '../../utils/theme';
import { useSocket } from '../../hooks/useSocket';

const nav = [
  { to: '/',          icon: '🏠', label: 'Dashboard' },
  { to: '/machines',  icon: '🖥️',  label: 'Máquinas' },
  { to: '/games',     icon: '🎮', label: 'Jogos' },
  { to: '/packages',  icon: '🎟️', label: 'Pacotes' },
  { to: '/products',  icon: '🍔', label: 'Lanchonete' },
  { to: '/orders',    icon: '🔔', label: 'Fila de pedidos' },
  { to: '/clients',   icon: '👥', label: 'Clientes' },
  { to: '/crm',       icon: '📊', label: 'CRM / Vendas' },
  { to: '/finance',   icon: '💰', label: 'Financeiro' },
  { to: '/reports',   icon: '📊', label: 'Relatórios' },
  { to: '/bookings/calendar', icon: '📅', label: 'Agenda de Eventos' },
  { to: '/memberships', icon: '🔁', label: 'Assinaturas' },
  { to: '/clans', icon: '🏆', label: 'Clãs' },
  { to: '/tournaments', icon: '⚔️', label: 'Campeonatos' },
  { to: '/settings',  icon: '⚙️', label: 'Configurações' },
];

export default function Layout({ children }) {
  const { operator, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logo, setLogo] = useState('');
  const [avatar, setAvatar] = useState(operator?.avatar_url || '');
  const [orderAlert, setOrderAlert] = useState(null);
  const [reservationAlert, setReservationAlert] = useState(null);
  const seenOrderIds = useRef(new Set());

  const playReservationSound = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContext();
      [0, .22, .44].forEach((delay, index) => {
        const oscillator = ctx.createOscillator(), gain = ctx.createGain();
        oscillator.type = 'sine'; oscillator.frequency.value = index === 2 ? 1046 : 784;
        gain.gain.setValueAtTime(.0001, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(.2, ctx.currentTime + delay + .02);
        gain.gain.exponentialRampToValueAtTime(.0001, ctx.currentTime + delay + .16);
        oscillator.connect(gain); gain.connect(ctx.destination);
        oscillator.start(ctx.currentTime + delay); oscillator.stop(ctx.currentTime + delay + .18);
      });
      window.setTimeout(() => ctx.close(), 1000);
    } catch (_) {}
  };

  const showReservationAlert = data => {
    if (!data?.id) return;
    if (operator?.tenant_id && Number(data.tenant_id) !== Number(operator.tenant_id)) return;
    const stage = data.notice_stage || (new Date(data.starts_at) <= new Date() ? 'started' : 'soon');
    const key = `arena-reservation-notice:${data.id}:${stage}`;
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, '1');
    setReservationAlert({...data,notice_stage:stage});
    playReservationSound();
  };

  useSocket((event, data) => {
    if (event === 'booking:reminder') return showReservationAlert(data);
    if (event !== 'snack:order_created') return;
    if (operator?.tenant_id && Number(data.tenant_id) !== Number(operator.tenant_id)) return;
    seenOrderIds.current.add(Number(data.id));
    setOrderAlert(data);
  });

  // Rede de segurança: se o Controller estava desconectado no instante do
  // pedido, consulta a fila e exibe o primeiro pedido novo assim que reconectar.
  useEffect(() => {
    let active = true;
    const checkOrders = async () => {
      try {
        const pending = (await orderService.getQueue()).data || [];
        if (!active) return;
        const next = pending.find(order => !seenOrderIds.current.has(Number(order.id)));
        if (next) {
          seenOrderIds.current.add(Number(next.id));
          setOrderAlert(next);
        }
      } catch (_) {}
    };
    checkOrders();
    const timer = window.setInterval(checkOrders, 10000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);

  // Se o controller estava fechado quando o aviso foi emitido, recupera a
  // reserva ao abrir qualquer página do painel dentro dos 30 minutos anteriores.
  useEffect(() => {
    let active = true;
    const checkReservations = async () => {
      try {
        const now = Date.now(), rows = (await bookingService.special()).data || [];
        if (!active) return;
        const next = rows.find(event => {
          const starts = new Date(event.starts_at).getTime();
          return starts >= now - 60000 && starts <= now + 30 * 60000;
        });
        if (next) showReservationAlert(next);
      } catch (_) {}
    };
    checkReservations();
    const timer = window.setInterval(checkReservations, 60000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const closeDrawer = () => setDrawerOpen(false);
  const goSettings = () => { closeDrawer(); navigate('/settings'); };
  const orderModal = orderAlert && <div style={styles.orderOverlay}>
    <div style={styles.orderModal}>
      <div style={styles.orderEyebrow}>NOVO PEDIDO DA LANCHONETE</div>
      <div style={styles.orderClient}>{orderAlert.client_name || orderAlert.nickname || 'CLIENTE'}</div>
      <div style={styles.orderPc}>{orderAlert.machine_name || 'PC'}</div>
      <div style={styles.orderAsked}>PEDIU UM</div>
      <div style={styles.orderProduct}>{orderAlert.product_name}</div>
      <div style={styles.orderButtons}>
        <button style={styles.orderQueue} onClick={() => { setOrderAlert(null); navigate('/orders'); }}>ABRIR FILA</button>
        <button style={styles.orderOk} onClick={() => setOrderAlert(null)}>ENTENDI</button>
      </div>
    </div>
  </div>;
  const reservationModal = reservationAlert && <div style={styles.reservationOverlay}>
    <div style={styles.reservationModal}>
      <div style={styles.reservationIcon}>📅</div>
      <div style={styles.reservationEyebrow}>{reservationAlert.notice_stage==='started'?'RESERVA INICIANDO AGORA':'RESERVA SE APROXIMANDO'}</div>
      <div style={styles.reservationTitle}>{reservationAlert.title}</div>
      <div style={styles.reservationTime}>{reservationAlert.date_label||new Date(reservationAlert.starts_at).toLocaleDateString('pt-BR')} · {reservationAlert.start_label||new Date(reservationAlert.starts_at).toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})} às {reservationAlert.end_label||new Date(reservationAlert.ends_at).toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})}</div>
      <div style={styles.reservationMachines}>{reservationAlert.machines||`${(reservationAlert.machine_ids||[]).length} equipamento(s) reservado(s)`}</div>
      <div style={styles.orderButtons}>
        <button style={styles.orderQueue} onClick={() => { setReservationAlert(null); navigate('/bookings/calendar'); }}>ABRIR AGENDA</button>
        <button style={styles.orderOk} onClick={() => setReservationAlert(null)}>CIENTE</button>
      </div>
    </div>
  </div>;

  // Carrega a cor de destaque e a logo desta lan house
  useEffect(() => {
    settingsService.get().then(r => {
      applyAccent(r.data.settings?.theme_accent);
      setLogo(r.data.settings?.logo_url || '');
    }).catch(() => {});
  }, []);

  // Foto atualizada do operador logado (localStorage pode estar desatualizado)
  useEffect(() => {
    if (!operator?.id) return;
    teamService.getAll()
      .then(r => { const me = (r.data || []).find(o => o.id === operator.id); if (me) setAvatar(me.avatar_url || ''); })
      .catch(() => {});
  }, [operator?.id]);

  // Conteúdo do menu (reutilizado no sidebar desktop e no drawer mobile)
  const menu = (
    <>
      <div style={styles.brand}>
        <img src={logo || '/logo.png'} alt="Logo" style={styles.brandLogo} />
      </div>

      <nav style={styles.nav}>
        {nav.map(item => (
          <NavLink
            key={`${item.to}-${item.label}`}
            to={item.to}
            end={item.to === '/'}
            onClick={closeDrawer}
            style={({ isActive }) => ({ ...styles.navItem, ...(item.sub ? styles.navSub : {}), ...(isActive ? styles.navActive : {}) })}
          >
            <span style={styles.navIcon}>{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div style={styles.bottom}>
        <div style={{ ...styles.operator, cursor: 'pointer' }} onClick={goSettings} title="Meu perfil / Configurações">
          {avatar
            ? <img src={avatar} alt={operator?.name} style={{ ...styles.operatorAvatar, objectFit: 'cover', padding: 0 }} />
            : <div style={styles.operatorAvatar}>{operator?.name?.charAt(0).toUpperCase()}</div>}
          <div>
            <div style={styles.operatorName}>{operator?.name}</div>
            <div style={styles.operatorRole}>{operator?.role === 'admin' ? 'Administrador' : 'Operador'}</div>
          </div>
        </div>
        <button onClick={handleLogout} style={styles.logoutBtn}>Sair</button>
      </div>
    </>
  );

  // ---------- MOBILE ----------
  if (isMobile) {
    return (
      <div style={styles.mobileWrapper}>
        <header style={styles.topbar}>
          <button style={styles.hamburger} onClick={() => setDrawerOpen(true)} aria-label="Menu">☰</button>
          <div style={styles.topbarBrand}>
            <img src="/logo.png" alt="Arena" style={{ height: 26 }} />
          </div>
          <div style={styles.topbarAvatar}>{operator?.name?.charAt(0).toUpperCase()}</div>
        </header>

        {drawerOpen && (
          <>
            <div style={styles.scrim} onClick={closeDrawer} />
            <aside style={{ ...styles.sidebar, ...styles.drawer }}>{menu}</aside>
          </>
        )}

        <main style={styles.mainMobile}>{children}</main>
        {orderModal}
        {reservationModal}
      </div>
    );
  }

  // ---------- DESKTOP ----------
  return (
    <div style={styles.wrapper}>
      <aside style={styles.sidebar}>{menu}</aside>
      <main style={styles.main}>{children}</main>
      {orderModal}
      {reservationModal}
    </div>
  );
}

const styles = {
  wrapper: { display: 'flex', width: '100%', height: '100dvh', overflow: 'hidden', background: '#0a0a0f', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' },
  mobileWrapper: { display: 'flex', flexDirection: 'column', width: '100%', height: '100dvh', minHeight: 0, overflow: 'hidden', background: '#0a0a0f', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', paddingBottom:'env(safe-area-inset-bottom)', boxSizing:'border-box' },
  sidebar: { position: 'sticky', top: 0, width: 220, height: '100dvh', overflow: 'hidden', background: '#14141f', borderRight: '1px solid #262636', display: 'flex', flexDirection: 'column', flexShrink: 0, boxSizing: 'border-box' },
  drawer: { position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100, boxShadow: '2px 0 24px rgba(0,0,0,0.5)' },
  scrim: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 99 },
  // Top bar (mobile)
  topbar: { position: 'relative', zIndex: 40, flexShrink: 0, minHeight: 'calc(56px + env(safe-area-inset-top))', background: '#14141f', borderBottom: '1px solid #262636', display: 'flex', alignItems: 'center', gap: 12, padding:'env(safe-area-inset-top) max(14px,env(safe-area-inset-right)) 0 max(14px,env(safe-area-inset-left))' },
  hamburger: { background: 'transparent', border: 'none', color: '#e5e7eb', fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: 4 },
  topbarBrand: { display: 'flex', alignItems: 'center', gap: 8, flex: 1 },
  topbarName: { color: '#fff', fontWeight: 800, fontSize: 16, letterSpacing: 1 },
  topbarAvatar: { width: 32, height: 32, borderRadius: '50%', background: '#1a2e05', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 },
  brand: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 18px', borderBottom: '1px solid #262636' },
  brandLogo: { width: '100%', maxWidth: 180, height: 'auto' },
  nav: { flex: 1, minHeight: 0, padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'thin', scrollbarColor: '#3b3b4e transparent' },
  navItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, color: '#9ca3af', textDecoration: 'none', fontSize: 14, fontWeight: 500, transition: 'all 0.15s' },
  navActive: { background: '#1a2e05', color: 'var(--accent)' },
  navIcon: { fontSize: 18, width: 22, textAlign: 'center' },
  navSub: { marginLeft: 25, paddingTop: 7, paddingBottom: 7, fontSize: 12, borderLeft: '1px solid #343445', borderRadius: '0 7px 7px 0' },
  bottom: { padding: '16px 12px', borderTop: '1px solid #262636' },
  operator: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  operatorAvatar: { width: 34, height: 34, borderRadius: '50%', background: '#1a2e05', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 },
  operatorName: { color: '#e5e7eb', fontSize: 13, fontWeight: 600 },
  operatorRole: { color: '#6b7280', fontSize: 11 },
  logoutBtn: { width: '100%', padding: '8px', background: 'transparent', border: '1px solid #374151', borderRadius: 6, color: '#9ca3af', cursor: 'pointer', fontSize: 13 },
  main: { flex: 1, minWidth: 0, minHeight: 0, height: '100%', maxWidth: 'calc(100vw - 220px)', overflowX: 'hidden', overflowY: 'auto', WebkitOverflowScrolling: 'touch', touchAction:'pan-y', overscrollBehaviorY: 'auto', padding: 28, boxSizing: 'border-box' },
  mainMobile: { flex: 1, width: '100%', maxWidth: '100vw', minWidth:0, minHeight:0, overflowX:'hidden', overflowY:'auto', WebkitOverflowScrolling:'touch', touchAction:'pan-y', overscrollBehaviorY:'auto', padding:'16px max(12px,env(safe-area-inset-right)) 16px max(12px,env(safe-area-inset-left))', boxSizing:'border-box' },
  orderOverlay:{position:'fixed',inset:0,zIndex:500,background:'rgba(0,0,0,.78)',display:'grid',placeItems:'center',padding:20},
  orderModal:{width:430,maxWidth:'92vw',background:'#11111a',border:'2px solid #a3e635',borderRadius:18,padding:30,textAlign:'center',boxShadow:'0 0 60px rgba(163,230,53,.25)'},
  orderEyebrow:{color:'#a3e635',fontSize:12,fontWeight:900,letterSpacing:2},orderClient:{color:'#fff',fontSize:29,fontWeight:900,marginTop:20,textTransform:'uppercase'},orderPc:{color:'#d4d4d8',fontSize:21,fontWeight:800,marginTop:5,textTransform:'uppercase'},orderAsked:{color:'#71717a',fontSize:13,fontWeight:800,letterSpacing:2,marginTop:24},orderProduct:{color:'#a3e635',fontSize:35,fontWeight:950,marginTop:5,textTransform:'uppercase'},orderButtons:{display:'flex',gap:10,marginTop:28},orderQueue:{flex:1,background:'#a3e635',color:'#0a0a0f',border:0,borderRadius:9,padding:12,fontWeight:900,cursor:'pointer'},orderOk:{background:'transparent',color:'#e5e7eb',border:'1px solid #52525b',borderRadius:9,padding:'12px 18px',fontWeight:800,cursor:'pointer'},
  reservationOverlay:{position:'fixed',inset:0,zIndex:520,background:'rgba(0,0,0,.82)',display:'grid',placeItems:'center',padding:20},
  reservationModal:{width:500,maxWidth:'92vw',boxSizing:'border-box',background:'#11111a',border:'2px solid #a3e635',borderRadius:18,padding:30,textAlign:'center',boxShadow:'0 0 70px rgba(163,230,53,.28)'},
  reservationIcon:{fontSize:42},reservationEyebrow:{color:'#a3e635',fontSize:12,fontWeight:900,letterSpacing:2,marginTop:10},reservationTitle:{color:'#fff',fontSize:29,fontWeight:950,marginTop:17,textTransform:'uppercase'},reservationTime:{color:'#e5e7eb',fontSize:18,fontWeight:800,marginTop:12},reservationMachines:{color:'#a3a3b2',fontSize:14,marginTop:9},
};
